import Badge from '@/app/common/components/Badge'
import { MARKETPLACE_BUSINESS_CATEGORIES } from '@/app/pages/marketplace/constants'
import { getAcceptingNewClientsLabelKey } from '@/app/pages/marketplace/utils'
import Image from '@/common/components/Image/Image'
import { MarketplaceStore } from '@/queries/marketplace/schema'
import { useTranslation } from 'react-i18next'

type StoreCardProps = {
  store: MarketplaceStore
  handleStoreClick: (store: MarketplaceStore) => void
}

const StoreCard = ({ handleStoreClick, store }: StoreCardProps) => {
  const { t } = useTranslation()
  const businessCategoryName =
    store?.provider_application?.business_category_name ?? ''
  const isAcceptingNewClients = store.accepting_new_clients === 1
  const acceptingStatusLabelKey = getAcceptingNewClientsLabelKey({
    isAcceptingNewClients,
    businessCategoryName,
  })
  const getItemLabel = (type: string): string => {
    const numberOfItems = store.number_of_items_in_store

    if (
      type === MARKETPLACE_BUSINESS_CATEGORIES.SCHOOL ||
      type === MARKETPLACE_BUSINESS_CATEGORIES.TEACHER
    ) {
      return numberOfItems === 1 ? 'Service' : 'Services'
    } else if (type === MARKETPLACE_BUSINESS_CATEGORIES.SHOP) {
      return numberOfItems === 1 ? 'Product' : 'Products'
    }
    return numberOfItems === 1 ? 'Item' : 'Items'
  }

  return (
    <button
      onClick={() => {
        handleStoreClick(store)
      }}
      key={store.id}
      className="border rounded-lg shadow-lg p-6 flex flex-col items-center text-center hover:shadow-xl transition-shadow duration-300"
    >
      <div className="mb-4">
        {store.logo ? (
          <Image
            className="object-contain size-[80px] border"
            path={store.logo}
          />
        ) : (
          <div className="h-[80px] w-[80px] bg-gray-200 flex items-center justify-center text-sm text-gray-500 border">
            No image
          </div>
        )}
      </div>

      <div className="font-semibold text-gray-800 text-base mb-4">
        {store?.name}
      </div>

      <div className="text-sm text-gray-500 mb-4">{businessCategoryName}</div>

      <div className="text-sm text-gray-600 font-medium border-t pt-2 w-full">
        {store.number_of_items_in_store} {getItemLabel(businessCategoryName)}
      </div>
      <Badge
        semanticColor={isAcceptingNewClients ? 'Positive' : 'Negative'}
        className="mt-2 whitespace-nowrap"
      >
        {t(acceptingStatusLabelKey)}
      </Badge>
    </button>
  )
}

export default StoreCard
